import { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DeleteOutlineOutlined, EditOutlined, MoreHoriz } from "@mui/icons-material";
import { theme } from "../../themes/theme";
import { formatDate } from "../../utils/StringUtils";
import { RuleOfWeekResponse } from "../../types/models/RuleOfWeekResponse";
import {
  getModuleNameFromTaxonomy,
  getContentNameFromTaxonomy,
} from "../../utils/RulesOfTheWeekUtils";
import EmptyResult from "../rules/EmptyResult";

interface Props {
  loading: boolean;
  ruleOfTheWeeks: RuleOfWeekResponse[] | undefined;
  filteredRuleOfTheWeeks: RuleOfWeekResponse[] | undefined;
  onEdit: (row: RuleOfWeekResponse) => void;
  onDelete: (id: string) => void;
}

const SkeletonRow = () => {
  return (
    <TableRow>
      {Array.from({ length: 7 }, (_, i) => (
        <TableCell key={i} height={"52px"}>
          <Skeleton variant="text" height="1.5em" />
        </TableCell>
      ))}
    </TableRow>
  );
};

export default function RulesOfTheWeekTable({
  loading,
  ruleOfTheWeeks,
  filteredRuleOfTheWeeks,
  onEdit,
  onDelete,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentDropdownRow, setCurrentDropdownRow] = useState<RuleOfWeekResponse | undefined>(
    undefined
  );

  const open = Boolean(anchorEl);

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setCurrentDropdownRow(undefined);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
            <TableRow>
              <TableCell>Display Name</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : (ruleOfTheWeeks?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell sx={{ p: 0 }} colSpan={7}>
                  <EmptyResult>No rules of the week available</EmptyResult>
                </TableCell>
              </TableRow>
            ) : filteredRuleOfTheWeeks?.length === 0 ? (
              <TableRow>
                <TableCell sx={{ p: 0 }} colSpan={7}>
                  <EmptyResult>No matching rule of the weeks found</EmptyResult>
                </TableCell>
              </TableRow>
            ) : (
              filteredRuleOfTheWeeks?.map((row) => {
                const isExpired = row.ROTWEnd ? new Date(row.ROTWEnd) < new Date() : false;
                return (
                  <TableRow key={row.Id} sx={isExpired ? { "& td": { color: "gray" } } : undefined}>
                    <TableCell>{row.DisplayName}</TableCell>
                    <TableCell>{formatDate(row.ROTWStart)}</TableCell>
                    <TableCell>{row.ROTWEnd ? formatDate(row.ROTWEnd) : "-"}</TableCell>
                    <TableCell>{getModuleNameFromTaxonomy(row.TaxonomyTitle ?? "")}</TableCell>
                    <TableCell>
                      {getContentNameFromTaxonomy(row.TaxonomyTitle, row.ContentDisplayName)}
                    </TableCell>
                    <TableCell>{row.RuleDisplayName}</TableCell>
                    <TableCell sx={{ px: 1 }}>
                      <IconButton
                        id="dropdown-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setCurrentDropdownRow(row);
                        }}
                      >
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        id="dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseDropdown}
        MenuListProps={{ "aria-labelledby": "dropdown-button" }}
      >
        <MenuItem
          onClick={() => {
            if (currentDropdownRow) {
              handleCloseDropdown();
              onEdit(currentDropdownRow);
            }
          }}
        >
          <EditOutlined />
          <Box sx={{ ml: 1 }}>Edit</Box>
        </MenuItem>
        <MenuItem
          sx={{ color: theme.palette.primary.main }}
          onClick={() => {
            if (currentDropdownRow) {
              handleCloseDropdown();
              onDelete(currentDropdownRow.Id);
            }
          }}
        >
          <DeleteOutlineOutlined />
          <Box sx={{ ml: 1 }}>Delete</Box>
        </MenuItem>
      </Menu>
    </>
  );
}
